<script setup>
import { ref } from 'vue'
import axios from 'axios'

const props = defineProps({
  initialDiscount: {
    type: [Number, String],
    default: 0
  },
  initialCode: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['notification', 'discountUpdated', 'updateCart'])

const promoCode = ref(props.initialCode)
const promoCodeLoading = ref(false)
const promoCodeMessage = ref('')
const promoCodeSuccess = ref(Boolean(props.initialCode))
const promoCodeApplied = ref(Boolean(props.initialCode))
const promoCodeDiscount = ref(props.initialDiscount)

const formatPrice = (price) => {
  return Number(price || 0).toFixed(2).replace('.', ',')
}

const updateFromCart = (updatedCart) => {
  promoCodeDiscount.value = updatedCart.discount_amount || 0
  promoCode.value = updatedCart.coupon_code || ''
  promoCodeApplied.value = Boolean(updatedCart.coupon_code)

  emit('discountUpdated', promoCodeDiscount.value)
  emit('updateCart', updatedCart)
}

const applyPromoCode = async () => {
  const code = promoCode.value.trim()

  if (!code || promoCodeLoading.value) return

  promoCodeLoading.value = true
  promoCodeMessage.value = ''

  try {
    const response = await axios.post('/api/checkout/cart/coupon', { code })
    const updatedCart = response.data.data

    updateFromCart(updatedCart)
    promoCodeSuccess.value = true
    promoCodeMessage.value = response.data.message || 'Code promo appliqué avec succès !'

    emit('notification', promoCodeMessage.value, 'success')
  } catch (error) {
    promoCodeSuccess.value = false
    promoCodeApplied.value = false
    promoCodeMessage.value = error.response?.data?.message || 'Code promo invalide ou expiré.'
    console.error('Erreur lors de l\'application du code promo:', error)

    emit('notification', promoCodeMessage.value, 'error')
  } finally {
    promoCodeLoading.value = false
  }
}

const removePromoCode = async () => {
  if (promoCodeLoading.value) return

  promoCodeLoading.value = true
  promoCodeMessage.value = ''

  try {
    const response = await axios.delete('/api/checkout/cart/coupon')
    const updatedCart = response.data.data

    updateFromCart(updatedCart)
    promoCodeSuccess.value = true
    promoCodeMessage.value = response.data.message || 'Code promo supprimé.'

    emit('notification', promoCodeMessage.value, 'success')
  } catch (error) {
    promoCodeSuccess.value = false
    promoCodeMessage.value = error.response?.data?.message || 'Erreur lors de la suppression du code promo.'
    emit('notification', promoCodeMessage.value, 'error')
  } finally {
    promoCodeLoading.value = false
  }
}
</script>

<template>
  <div class="promo-code-section">
    <h4 class="section-title">
      <i class="bi bi-tag-fill me-2"></i>Code promo
    </h4>
    <p class="text-muted mb-3">Vous avez un code promo ? Entrez-le ci-dessous pour bénéficier
      d'une réduction</p>

    <div class="promo-code-container">
      <div class="promo-code-form" :class="{'is-loading': promoCodeLoading}">
        <div class="input-group">
          <span class="input-group-text">
            <i class="bi bi-gift"></i>
          </span>
          <input
            type="text"
            class="form-control"
            v-model="promoCode"
            placeholder="Saisissez votre code promo"
            :disabled="promoCodeLoading"
          >
          <button
            class="btn btn-apply-code"
            type="button"
            @click="applyPromoCode"
            :disabled="!promoCode || promoCodeLoading"
          >
            <span v-if="!promoCodeLoading">Appliquer</span>
            <span v-else class="spinner-border spinner-border-sm" role="status"
                  aria-hidden="true"></span>
          </button>
        </div>
      </div>

      <div v-if="promoCodeMessage" class="promo-code-message mt-2"
           :class="{'is-success': promoCodeSuccess, 'is-error': !promoCodeSuccess}">
        <i :class="['bi', promoCodeSuccess ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill', 'me-2']"></i>
        {{ promoCodeMessage }}
      </div>

      <div v-if="promoCodeApplied" class="applied-promo-code mt-3">
        <div class="applied-code-details">
          <div class="code-badge">
            <i class="bi bi-check-circle-fill me-1"></i>
            {{ promoCode }}
          </div>
          <div class="discount-amount">
            -{{ formatPrice(promoCodeDiscount) }}€
          </div>
        </div>
        <button class="btn-remove-code" @click="removePromoCode">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>
  </div>
</template>
